/**
 * createEditorCommands — headless Solid primitive that returns a typed
 * facade over Engine.dispatch(). Components call e.g.
 * `cmd.setZoom(1.25)` instead of building Command JSON by hand.
 *
 * Every method here corresponds 1:1 to a variant of `Command` in
 * `crates/bridge/src/command.rs` — the generated TS shapes live in
 * `crates/engine-wasm/pkg/engine_wasm.d.ts`. Add new methods here when
 * the bridge grows; do not let downstream UI assemble raw Command objects.
 * Issue #338 — and classify the method in `facadeMap.ts`: its tsc-checked
 * maps refuse a method that dispatches a command the engine only stubs
 * (`StubCommandType`, generated from `crates/bridge/src/meta.rs`).
 *
 * tsify-next renders `Option<T>` as `T | undefined`. With
 * `exactOptionalPropertyTypes: true` every field must be set explicitly,
 * so `emptyPatch()` provides the canonical zero state for `TextAttrsPatch`.
 *
 * Formatting helpers (setBold, setUnderline, …) omit `range` by default so
 * the ENGINE binds the patch to its own live selection — the UI mirror is
 * async and can be stale while a mutation is in flight. Paragraph / caret
 * helpers still resolve from an internal `createEditorState` subscription.
 * Pass an explicit `range` to override.
 */
import { useEngine, useDocumentDefaults, type EngineHandle } from './EngineProvider';
import { clearOpenWarnings, createEditorState, type EditorState } from './createEditorState';
import type {
    Command,
    Event,
    DocFormat,
    DocumentDefaults,
    TextAttrsPatch,
    UnderlineStyle,
    VerticalScript,
    FormattingToggle,
    FontSlot,
    Alignment,
    Direction,
    PdfConformance,
    ImageBlob,
    ImageFit,
    ImageWrapMode,
    TextBoxHop,
    MoveDirection,
    LogicalPos,
    LogicalRange,
    Rect,
    BlockPath,
    Color,
    BridgeCellBorders,
    TablePropertiesPatch,
    BridgeTabStop,
    InsertSide,
    PageOrientation,
    SectionBreakKind,
    HeaderFooterArea,
    FieldKind,
    TocSwitches,
    ListKind,
    BridgeStyleProperties,
} from './types';
import type { LiveCommand } from './facadeMap';

/** Issue #339 — the `DocFormat` a file opens as, from its name: `.txt` →
 *  `plain_text`, `.html` / `.htm` / `.xhtml` → `html`, anything else (and no
 *  name) → `docx`. PDF is an export-only format and never inferred. */
export function docFormatForFileName(name: string | undefined): DocFormat {
    const ext = name?.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
    switch (ext) {
        case 'txt':
        case 'text':
            return 'plain_text';
        case 'html':
        case 'htm':
        case 'xhtml':
            return 'html';
        default:
            return 'docx';
    }
}

/** Sprint 12 (#11) — paragraph style id. The engine now models real
 *  `<w:styles>` entries on `DocumentTree.styles`; `cmd.applyStyle`
 *  dispatches `Command::ApplyStyle { style_id }` and the engine
 *  cascades the style's `<w:pPr>` into the resolved paragraph
 *  properties. `string` because the live set comes from the loaded
 *  document's stylesheet — not a closed UI enum. */
export type ParagraphStyleId = string;

/** Sprint 5 → 12 — stock preset values used as a FALLBACK only.
 *  When the user picks a heading-style id that the loaded document
 *  doesn't ship (a brand-new file with no `styles.xml` shipped
 *  customizations), the UI degrades to a direct-formatting patch via
 *  the legacy `cmd.applyAttrs` path so the user still sees a visible
 *  change. The real engine path is `Command::ApplyStyle`. */
export const STYLE_PRESETS: Record<string, Partial<TextAttrsPatch>> = {
    Normal: { bold: false, italic: false, font_size: 12 },
    Title: { bold: true, italic: false, font_size: 32 },
    Heading1: { bold: true, italic: false, font_size: 24 },
    Heading2: { bold: true, italic: false, font_size: 20 },
    Heading3: { bold: true, italic: false, font_size: 16 },
};

/** Canonical empty `TextAttrsPatch` — every field set to `undefined`. */
export function emptyPatch(): TextAttrsPatch {
    return {
        bold: undefined,
        italic: undefined,
        underline: undefined,
        strike: undefined,
        font_family: undefined,
        font_size: undefined,
        color: undefined,
        bg_color: undefined,
        script: undefined,
        language: undefined,
        caps: undefined,
        small_caps: undefined,
    };
}

/** Issue #221 — canonical empty `DocumentDefaults`, every field
 *  `undefined`. `exactOptionalPropertyTypes` requires both keys present
 *  (as a value or explicit `undefined`) on any object typed as
 *  `DocumentDefaults`, so a partial override spreads onto this — e.g.
 *  `{ ...emptyDocumentDefaults(), page_size: 'Letter' }` — the same
 *  pattern `emptyPatch()` provides for `TextAttrsPatch`. */
export function emptyDocumentDefaults(): DocumentDefaults {
    return { page_size: undefined, widow_control: undefined };
}

/**
 * Issue #420 — the per-slot formatting `EditorCommands.setSlotFormat`
 * writes: every field optional, only the present ones are applied.
 * `fontFamily` is a font-registry id (`FontRegistry.fonts()[n].id`).
 */
export interface SlotFormatPatch {
    fontFamily?: string;
    fontSize?: number;
    bold?: boolean;
    italic?: boolean;
}

export interface EditorCommands {
    /* Lifecycle */
    requestStats(): Promise<Event>;
    /** Issue #240 — whether the engine offers `retryGpuRenderer`. */
    readonly canRetryGpuRenderer: boolean;
    /** Issue #240 — forget a GPU crash-loop downgrade and probe the GPU
     *  renderer again. Issue #270 — in place: the engine restarts as a
     *  recovery generation (no reload), so the document, selection, undo
     *  and zoom survive. A no-op when `canRetryGpuRenderer` is false. */
    retryGpuRenderer(): Promise<void>;
    /** Issue #428 - whether the engine offers an in-place `setRenderer`. */
    readonly canSetRenderer: boolean;
    /** Issue #428 - switch the render backend in place (same path as
     *  `retryGpuRenderer`, #270): no reload, the document survives. A
     *  no-op when `canSetRenderer` is false. */
    setRenderer(kind: 'vello' | 'canvas2d'): Promise<void>;
    requestPaint(viewport: Rect, dirty?: Rect): Promise<Event>;

    /* Viewport */
    setZoom(scale: number): Promise<Event>;
    /** Post-boot devicePixelRatio change — updates the device scale the
     *  engine composes with the user zoom (`scale = device × zoom`). */
    setDeviceScale(scale: number): Promise<Event>;
    setViewport(rect: Rect): Promise<Event>;
    expandLayout(targetY: number): Promise<Event>;

    /* History */
    undo(): Promise<Event>;
    redo(): Promise<Event>;

    /* Text mutation */
    insertText(text: string, at?: LogicalPos): Promise<Event>;
    deleteRange(range: LogicalRange): Promise<Event>;
    replaceRange(range: LogicalRange, text: string): Promise<Event>;
    deleteAtCaret(forward: boolean, byWord?: boolean): Promise<Event>;
    /** Split the paragraph. Issue #64 — omit `at` to split at the
     *  engine's LIVE caret (the race-free interactive path). */
    splitParagraph(at?: LogicalPos): Promise<Event>;
    /**
     * Insert a soft line break (Shift+Enter) at `at` (defaults to the
     * current caret). A soft break wraps to the next line WITHOUT
     * splitting the paragraph block — it inserts a U+2028 LINE SEPARATOR
     * into the existing text run, so the layout engine forces a new line
     * there while keeping one paragraph. The first-line indent applies to
     * the paragraph's absolute first line only, so the soft-broken
     * continuation hugs the left indent (Word / Google Docs behaviour).
     * Contrast `splitParagraph`, which creates a new paragraph block.
     */
    insertSoftBreak(at?: LogicalPos): Promise<Event>;

    /* Selection */
    selectAll(): Promise<Event>;
    setSelection(range: LogicalRange, caret: LogicalPos): Promise<Event>;
    extendSelection(to: LogicalPos): Promise<Event>;
    moveCaret(direction: MoveDirection, extend?: boolean): Promise<Event>;

    /* Formatting — `range` defaults to the engine-owned live selection. */
    applyAttrs(attrs: Partial<TextAttrsPatch>, range?: LogicalRange): Promise<Event>;
    /** Issue #420 — `slot` picks the script slot, as for `setFontSize`:
     *  omitted (or `'Both'`, Word's ribbon) = `<w:b>` AND `<w:bCs>`;
     *  `'Latin'` = `<w:b>` only; `'ComplexScript'` = `<w:bCs>` only. */
    setBold(value: boolean, range?: LogicalRange, slot?: FontSlot): Promise<Event>;
    /** Issue #420 — `slot` as for `setBold` (`<w:i>` / `<w:iCs>`). */
    setItalic(value: boolean, range?: LogicalRange, slot?: FontSlot): Promise<Event>;
    setStrike(value: boolean, range?: LogicalRange): Promise<Event>;
    setUnderline(style: UnderlineStyle, range?: LogicalRange): Promise<Event>;
    setVerticalScript(script: VerticalScript, range?: LogicalRange): Promise<Event>;
    /** Issue #420 — `slot` as for `setBold`: `'Latin'` names
     *  `w:ascii` / `w:hAnsi` only, `'ComplexScript'` `w:cs` only (the
     *  Arabic face), omitted / `'Both'` all three. */
    setFontFamily(family: string, range?: LogicalRange, slot?: FontSlot): Promise<Event>;
    /** Issue #359 — `slot` picks the script slot the size writes: omitted
     *  (or `'Both'`) sets Latin AND complex-script text, as Word's ribbon
     *  does; `'Latin'` = `<w:sz>` only; `'ComplexScript'` = `<w:szCs>`
     *  only (Arabic / Hebrew / Thai text). */
    setFontSize(pt: number, range?: LogicalRange, slot?: FontSlot): Promise<Event>;
    /**
     * Issue #420 — family / size / weight / slant of ONE script slot in a
     * single `APPLY_FORMATTING` (one undo step): the Font dialog's
     * "Latin text" (`'Latin'`) or "Complex scripts" (`'ComplexScript'`)
     * section. Only the fields present are written, so a mixed selection
     * keeps everything the user did not touch. A collapsed caret arms
     * the formatting for the next keystroke (pending style), like every
     * formatting helper.
     */
    setSlotFormat(slot: FontSlot, format: SlotFormatPatch, range?: LogicalRange): Promise<Event>;
    setColor(r: number, g: number, b: number, a?: number, range?: LogicalRange): Promise<Event>;
    setHighlight(r: number, g: number, b: number, a?: number, range?: LogicalRange): Promise<Event>;
    /** `<w:caps/>` — render every glyph uppercase. */
    setCaps(value: boolean, range?: LogicalRange): Promise<Event>;
    /** `<w:smallCaps/>` — render lowercase as smaller uppercase glyphs. */
    setSmallCaps(value: boolean, range?: LogicalRange): Promise<Event>;
    /**
     * Issue #286 — flip `attr` on the engine's LIVE selection. The engine
     * computes the target state from its own style at the caret (+ any
     * armed pending style), never from this shell's mirrored toolbar
     * state, which lags the reply at typing speed. Toolbar toggles and
     * keyboard shortcuts must use this, not `setBold(!isBold())`.
     * `underlineStyle` is the style used when underline turns ON.
     */
    toggleFormatting(attr: FormattingToggle, underlineStyle?: UnderlineStyle): Promise<Event>;

    /* Paragraph — `range` defaults to current selection. */
    setParagraphAlign(align: Alignment, range?: LogicalRange): Promise<Event>;
    setParagraphDirection(direction: Direction, range?: LogicalRange): Promise<Event>;

    /* Images */
    insertImage(image: ImageBlob, at: LogicalPos, fit?: ImageFit): Promise<Event>;
    insertImageAtCaret(image: ImageBlob, fit?: ImageFit): Promise<Event>;
    /** Issue #44 — overwrite the display extent of the inline image at
     *  `(path, at)` (its `U+FFFC` sentinel byte offset). Dimensions are
     *  EMU, the model's native `<wp:extent>` unit. */
    resizeImage(
        path: BlockPath,
        at: number,
        widthEmu: number,
        heightEmu: number,
        story?: TextBoxHop[],
    ): Promise<Event>;
    /** Issue #69 — reposition the FLOATING (`<wp:anchor>`) image at
     *  `(path, at)`: both axes become fixed EMU offsets inside their
     *  current reference frames (`relativeFrom` preserved; an alignment /
     *  percentage placement is replaced, `simplePos` switched off). The
     *  engine answers `ERROR` for an inline image — only rects with
     *  `ImageRect.floating` accept a move. */
    moveImage(
        path: BlockPath,
        at: number,
        offsetHEmu: number,
        offsetVEmu: number,
        story?: TextBoxHop[],
    ): Promise<Event>;
    /** Issue #82 — set the text-wrap mode of the FLOATING image at
     *  `(path, at)` (Word's "Wrap Text" menu: square, tight, through,
     *  top-and-bottom, behind text, in front of text). The body text is
     *  cut around the image on the next layout. The engine answers
     *  `ERROR` for an inline image — only rects with `ImageRect.floating`
     *  have a wrap mode (`ImageRect.wrap`). */
    setImageWrap(
        path: BlockPath,
        at: number,
        wrap: ImageWrapMode,
        story?: TextBoxHop[],
    ): Promise<Event>;
    /** Issue #44 — query every inline image's on-canvas rect + resize
     *  address. Resolve the `Event` and read `images` when it is an
     *  `IMAGE_RECTS` reply. Issue #206 — pictures inside text-box stories
     *  are listed too: pass a rect's `story` back as the optional `story`
     *  argument of `resizeImage` / `moveImage` / `setImageWrap`. */
    getImageRects(): Promise<Event>;

    /* Tables — every method maps 1:1 onto a `Command` variant in
     * `crates/bridge/src/command.rs`. */
    insertTable(at: BlockPath, rows: number, cols: number): Promise<Event>;
    insertTableAtCaret(rows: number, cols: number): Promise<Event>;
    deleteTable(path: BlockPath): Promise<Event>;
    /** Insert a row at `row` on the named `side` (Before | After).
     *  Sprint 2 (UI Edition) hotfix: the previous `(tablePath,
     *  afterRow)` shape forced callers into `row - 1` math that
     *  underflows `u32` on the wire when `row === 0`. */
    insertRow(tablePath: BlockPath, row: number, side: InsertSide): Promise<Event>;
    deleteRow(tablePath: BlockPath, row: number): Promise<Event>;
    /** Insert a column at `col` on the named `side` — same hotfix
     *  rationale as [`insertRow`]. */
    insertColumn(tablePath: BlockPath, col: number, side: InsertSide): Promise<Event>;
    deleteColumn(tablePath: BlockPath, col: number): Promise<Event>;
    mergeCells(
        tablePath: BlockPath,
        fromRow: number,
        fromCol: number,
        toRow: number,
        toCol: number,
    ): Promise<Event>;
    splitCell(tablePath: BlockPath, row: number, col: number): Promise<Event>;
    setCellShading(
        tablePath: BlockPath,
        row: number,
        col: number,
        color: Color | undefined,
    ): Promise<Event>;
    setCellBorders(
        tablePath: BlockPath,
        row: number,
        col: number,
        borders: BridgeCellBorders,
    ): Promise<Event>;
    /** Issue #79 — patch table-level properties (`<w:tblPr>`) of the
     *  top-level table at `tablePath`; `undefined` fields are left as is.
     *  Replies `SelectionChanged` (its `cell_properties.table_bidi_visual`
     *  carries the new RTL state). */
    setTableProperties(tablePath: BlockPath, patch: TablePropertiesPatch): Promise<Event>;
    /** Issue #79 — convenience: toggle `<w:bidiVisual>` (right-to-left
     *  visual column order; grid column 1 becomes the rightmost). */
    setTableBidiVisual(tablePath: BlockPath, bidiVisual: boolean): Promise<Event>;

    /* Layout authoring (Sprint 2 UI Edition — shipped with the
     * matching Rust bridge additions in `crates/bridge/src/command.rs`
     * + handlers in `crates/engine-wasm/src/lib.rs`). */
    setColumns(at: LogicalPos, count: number, gutterPt: number): Promise<Event>;
    setColumnsAtCaret(count: number, gutterPt?: number): Promise<Event>;
    /** Issue #75 — Word's Ctrl+Enter: insert a manual page break
     *  (`<w:br w:type="page"/>`) at the caret, replacing any selection.
     *  Rejected with `Event::Error` inside a table cell. */
    insertPageBreak(at?: LogicalPos): Promise<Event>;
    /** Phase 3 (#40) — insert a REAL section break (splits the caret
     *  paragraph into two sections; the following section begins per
     *  `kind`). Distinct from `insertPageBreak`, which inserts a manual
     *  page break (Word's Ctrl+Enter) inside the current section. The
     *  engine rejects table-cell carets with
     *  `Event::Error` — the Breaks menu disables those entries. */
    insertSectionBreak(kind: SectionBreakKind, at?: LogicalPos): Promise<Event>;
    /** Phase 3 (#39) — enter header/footer editing for the section that
     *  owns `page` (0-based). The engine guarantees the section owns a
     *  private part (materialize/fork on enter) and re-roots the
     *  selection into the story; `state.editingStory()` flips truthy. */
    enterHeaderFooter(page: number, area: HeaderFooterArea): Promise<Event>;
    /** Phase 3 (#39) — leave header/footer editing; the stashed body
     *  selection is restored (clamped). Issue #80: also leaves a
     *  footnote / endnote story. */
    exitHeaderFooter(): Promise<Event>;
    /** Issue #70 — Word's "Link to Previous" for the ACTIVE story's
     *  slot: `false` forks a private copy owned by the story's
     *  section; `true` clears the slot so it inherits again
     *  (Event::Error on the first section). Story-scoped. */
    setHeaderFooterLink(linked: boolean): Promise<Event>;
    /** Issue #74 — `<w:titlePg/>` on the covering section (the active
     *  story's when one is open, else the caret's). */
    setTitlePage(enabled: boolean): Promise<Event>;
    /** Issue #74 — document-wide `<w:evenAndOddHeaders/>`. */
    setEvenOddHeaders(enabled: boolean): Promise<Event>;
    /** Issue #43 — author a PAGE / NUMPAGES / DATE field at the caret
     *  (body or story; rejected inside table cells). */
    insertField(kind: FieldKind, at?: LogicalPos): Promise<Event>;
    /** Issue #77 — F9: re-resolve every field (body + every header /
     *  footer part) and stamp the live values into the document as ONE
     *  undo step. PAGE / NUMPAGES read a full pagination; DATE / TIME /
     *  FILENAME / AUTHOR read the shell-injected environment. A document
     *  whose fields are all current is left untouched. */
    updateFields(): Promise<Event>;
    /** Issue #77 — Alt+F9: paint `{ INSTRUCTION }` codes in place of
     *  results. Pure display state; `state.fieldCodeView()` mirrors it. */
    setFieldCodeView(enabled: boolean): Promise<Event>;
    /** Issue #77 — replace the code of the field the caret addresses
     *  (`state.fieldAtCaret()`); the cached result stays until the next
     *  update (Word parity). `at` defaults to the live caret; pass
     *  `fieldAtCaret().end` to address a field explicitly. */
    setFieldInstruction(instruction: string, at?: LogicalPos): Promise<Event>;
    /** Issue #81 — insert a Table of Contents at the caret (a top-level
     *  body paragraph; the engine rejects table cells, header / footer /
     *  note stories and carets inside an existing TOC with
     *  `Event::Error`). The entries are generated immediately from the
     *  document's headings with live page numbers; `updateFields()` (F9)
     *  regenerates them. Unset switches take Word's defaults
     *  (`TOC \o "1-3" \h \z \u`). */
    insertToc(switches?: Partial<TocSwitches>, at?: LogicalPos): Promise<Event>;
    /** Issue #80 — author a footnote referenced at the caret (body
     *  paragraphs only; the engine rejects table cells and story
     *  carets with `Event::Error`). The engine splices the reference,
     *  renumbers every later marker and ENTERS the new note, so
     *  `state.editingStory()?.area` becomes `'Footnote'`;
     *  `exitHeaderFooter()` returns to the body after the reference. */
    insertFootnote(at?: LogicalPos): Promise<Event>;
    /** Issue #80 — endnote twin of `insertFootnote`; endnotes collect
     *  at section / document end per `<w:endnotePr><w:pos>`. */
    insertEndnote(at?: LogicalPos): Promise<Event>;
    /** Issue #83 — insert a floating text box anchored at the caret
     *  (body paragraphs only; the engine rejects table cells and story
     *  carets with `Event::Error`). Extent in EMU (914400 per inch),
     *  default 2" × 1". The box gets Word's "Draw Text Box" defaults
     *  (white fill, 0.75 pt outline, square wrap) and the engine ENTERS
     *  its story, so `state.editingStory()?.area` becomes `'TextBox'`;
     *  a click outside the box or `exitHeaderFooter()` returns to the
     *  body. */
    insertTextBox(widthEmu?: number, heightEmu?: number, at?: LogicalPos): Promise<Event>;
    setParagraphBorders(
        borders: BridgeCellBorders,
        range?: LogicalRange,
    ): Promise<Event>;
    clearParagraphBorders(range?: LogicalRange): Promise<Event>;

    /* Lists (Sprint 5 UI Edition). `Off` clears `list_item` on every
     * paragraph the range spans; `Bullet` / `Number` run the engine's
     * idempotent `numbering.xml` synthesis (Sprint 13 #12). */
    toggleList(kind: ListKind, range?: LogicalRange): Promise<Event>;

    /* Paragraph formatting (Sprint 6 UI Edition). Units are points. */
    setParagraphIndent(
        startPt: number,
        endPt: number,
        firstLinePt: number,
        range?: LogicalRange,
    ): Promise<Event>;
    increaseIndent(stepPt?: number, range?: LogicalRange): Promise<Event>;
    decreaseIndent(stepPt?: number, range?: LogicalRange): Promise<Event>;
    setLineSpacing(multiplier: number, range?: LogicalRange): Promise<Event>;
    setParagraphShading(color: Color | undefined, range?: LogicalRange): Promise<Event>;
    /**
     * Sprint 11 (#13) — replace `<w:pPr><w:tabs>` on every paragraph
     * the range spans. Dispatch ONCE on `pointerup` after a Ruler
     * drag — never on `pointermove` — so one user gesture produces
     * exactly one undo entry.
     */
    setTabStops(stops: BridgeTabStop[], range?: LogicalRange): Promise<Event>;

    /* Review (Sprint 7 UI Edition). `toggleTrackChanges` flips the
     * engine's recording flag (Sprint 14 #14); the live state rides
     * back on `SELECTION_CHANGED.is_tracking_changes`. */
    toggleTrackChanges(enabled: boolean): Promise<Event>;
    /**
     * Sprint 14 (#14) — stamp `author` + `date` on every subsequent
     * tracked revision. Empty `date` leaves the engine's stored
     * date untouched so the worker can stamp `Date.now()` per
     * mutation; empty `author` keeps the prior value (default
     * `"You"`).
     */
    setReviewIdentity(author: string, date: string): Promise<Event>;
    /** Issue #85 — `Command::Snapshot`: the versioned engine snapshot
     *  (`engine::snapshot` envelope: document + styles + stories + undo
     *  window + selection + layout config). Read-only; resolves with the
     *  `SNAPSHOT` event (or `ERROR`). */
    snapshot(): Promise<Event>;
    /** Accept one tracked change. Issue #304 — pass the snapshot row's
     *  `revision_id` and it alone addresses the revision (the outer of
     *  two wrappers over one range included; a stale id is a no-op);
     *  without it the `(block, start, end)` range does. Either half of a
     *  tracked move resolves the whole move. */
    acceptRevision(
        block: number,
        start: number,
        end: number,
        revisionId?: number,
    ): Promise<Event>;
    /** Reject one tracked change — addressed like `acceptRevision`. */
    rejectRevision(
        block: number,
        start: number,
        end: number,
        revisionId?: number,
    ): Promise<Event>;
    /** Issue #262 — accept every tracked change of the document in ONE
     *  engine command (one undo step; paragraph-mark revisions merge
     *  paragraphs). */
    acceptAllRevisions(): Promise<Event>;
    /** Issue #262 — reject every tracked change in one engine command. */
    rejectAllRevisions(): Promise<Event>;
    insertComment(
        text: string,
        author: string,
        range?: LogicalRange,
    ): Promise<Event>;
    deleteComment(id: number): Promise<Event>;
    resolveComment(id: number, resolved: boolean): Promise<Event>;
    /**
     * Issue #27 — append a threaded reply to the comment with
     * `parentId`. The engine anchors the reply on the parent's range
     * and mints the new sequential id; an unknown parent returns
     * `Event::Error`. Deleting the parent cascades over its replies.
     */
    replyToComment(
        parentId: number,
        text: string,
        author: string,
    ): Promise<Event>;

    /* Styles (Sprint 12 #11). Dispatches `APPLY_STYLE` — the engine
     * sets `<w:pStyle>` and cascades the style's `<w:pPr>`. The
     * `STYLE_PRESETS` table remains a UI-side fallback for documents
     * that don't ship the picked style id. */
    applyStyle(styleId: ParagraphStyleId, range?: LogicalRange): Promise<Event>;
    /** Issue #21 — mutate an existing style DEFINITION (assignment is
     *  `applyStyle`); the engine re-cascades every dependent paragraph
     *  and regenerates styles.xml on the next save. */
    modifyStyle(styleId: ParagraphStyleId, properties: BridgeStyleProperties): Promise<Event>;

    /* Page setup (Sprint 4 UI Edition). All margin units are points. */
    setPageMargins(
        at: LogicalPos,
        topPt: number,
        rightPt: number,
        bottomPt: number,
        leftPt: number,
    ): Promise<Event>;
    setPageMarginsAtCaret(
        topPt: number,
        rightPt: number,
        bottomPt: number,
        leftPt: number,
    ): Promise<Event>;
    setPageOrientation(at: LogicalPos, orientation: PageOrientation): Promise<Event>;
    setPageOrientationAtCaret(orientation: PageOrientation): Promise<Event>;

    /* I/O */
    /** Open a document byte buffer (passed zero-copy as a Transferable).
     *  Issue #339 — `.docx`, plain text and HTML are all engine-real:
     *  `opts.format` picks the format, else it is inferred from `name`
     *  (`docFormatForFileName`: `.txt` → plain text, `.html`/`.htm` → HTML,
     *  otherwise `.docx`). Plain text opens one paragraph per line (UTF-8,
     *  UTF-16 with a BOM); HTML goes through the rich-paste parser (`dir`
     *  honoured, tables kept); paragraphs without a declared direction get
     *  their first-strong one, so Arabic opens RTL.
     *  Issue #239 — `opts.initialZoom`, when given, dispatches `SET_ZOOM`
     *  before `OPEN_DOCUMENT` so a host can request a starting zoom from
     *  this one call; the engine queues it even if no `RenderPage` has
     *  run yet.
     *  Issue #221 — `opts.defaults` (page size / widow control fallbacks
     *  for a `<w:sectPr>` / paragraph OOXML never carries an element for)
     *  rides straight onto `OPEN_DOCUMENT.defaults`; omitted, it falls
     *  back to the `<EngineProvider documentDefaults={...}>` host default
     *  (`useDocumentDefaults()`), and omitted there too, the wire field is
     *  left out entirely — `format_docx::read_docx`'s unchanged A4 /
     *  widow-control-ON behaviour. */
    /** Issue #345 — `opts.password` opens an encrypted (MS-OFFCRYPTO)
     *  `.docx`: without it such a file answers `ERROR { kind:
     *  'EncryptedDocument' }`, with a wrong one `kind: 'WrongPassword'`.
     *  `bytes` is TRANSFERRED to the worker — keep the source (`File`) to
     *  retry with a password. */
    openDocument(
        bytes: Uint8Array,
        name?: string,
        opts?: {
            initialZoom?: number;
            defaults?: DocumentDefaults;
            format?: DocFormat;
            password?: string;
        },
    ): Promise<Event>;
    saveDocument(format: DocFormat): Promise<Event>;
    saveDocx(): Promise<Event>;
    /** `A1b` / `A2u` / `X3`, or `Ua1` — issue #360: the tagged,
     *  accessible PDF/UA-1 export (on a PDF/A-2u base; `A2u` itself stays
     *  untagged). */
    exportPdf(conformance: PdfConformance): Promise<Event>;
    /** `SaveDocument { format: 'html' }` — `crates/format-html` (Sprint 9). */
    exportHtml(): Promise<Event>;
    /** `SaveDocument { format: 'plain_text' }` — `DocumentTree::to_plain_text`. */
    exportPlainText(): Promise<Event>;
    /** Issue #338 — back to the seeded empty document: selection, undo
     *  history, comments, revisions, media, the retained `.docx` source
     *  package and the document name are cleared (fonts, zoom and the
     *  review identity survive). Answers `SELECTION_CHANGED`; the worker
     *  then broadcasts the accessibility delta + `PAINTED` and pins the
     *  next event-log snapshot as the new document's base. */
    closeDocument(): Promise<Event>;

    /* Clipboard */
    /** Issue #57 — `{ includeDocx: false }` skips the engine's `.docx`
     *  fragment ZIP build (`docx_fragment` comes back empty); omitted ⇒
     *  the full payload. */
    getSelectionAsClipboard(opts?: { includeDocx?: boolean }): Promise<Event>;
    pastePlain(text: string): Promise<Event>;
    pasteHtml(html: string): Promise<Event>;

    /* Escape hatch — for commands not yet covered above. Issue #338 — typed
     * over LIVE commands only: a command the engine merely stubs does not
     * type-check here either. */
    raw(cmd: LiveCommand, transfer?: Transferable[]): Promise<Event>;
}

function build(
    engine: EngineHandle,
    state: EditorState,
    documentDefaults?: DocumentDefaults,
): EditorCommands {
    const dispatch = (cmd: Command, transfer: Transferable[] = []) =>
        engine.dispatch(cmd, transfer);

    /* Selection helpers — throw clearly if no selection exists yet, so
     * callers see a programming error rather than a silent no-op. */
    const currentRange = (override?: LogicalRange): LogicalRange => {
        if (override) return override;
        const sel = state.selection();
        if (!sel) {
            throw new Error(
                '[@nge/core] formatting command requires a range; no current selection (engine has not emitted SELECTION_CHANGED yet).',
            );
        }
        return sel;
    };
    const currentCaret = (): LogicalPos => {
        const sel = state.selection();
        if (!sel) {
            throw new Error(
                '[@nge/core] insert-at-caret requires a caret; no current selection yet.',
            );
        }
        return sel.end;
    };

    /* `undefined` range (never `null` — tsify renders `Option<T>` as
     * `T | undefined`) makes the ENGINE resolve its authoritative live
     * selection, so a stale UI mirror can never misbind formatting. The
     * cast bridges the wasm-pack d.ts until it regenerates with
     * `range: LogicalRange | undefined`. */
    const fmt = (patch: Partial<TextAttrsPatch>, range?: LogicalRange) =>
        dispatch({
            type: 'APPLY_FORMATTING',
            range: range as LogicalRange,
            attrs: { ...emptyPatch(), ...patch },
        });
    /* Issues #359 / #420 — an omitted slot stays off the wire (the engine
     * reads it as `Both`, Word's ribbon), so older engines see the exact
     * patch they always did. */
    const slotted = (patch: Partial<TextAttrsPatch>, slot?: FontSlot): Partial<TextAttrsPatch> =>
        slot === undefined ? patch : { ...patch, font_slot: slot };

    return {
        requestStats: () => dispatch({ type: 'REQUEST_STATS' }),
        canRetryGpuRenderer: typeof engine.retryGpuRenderer === 'function',
        retryGpuRenderer: () => engine.retryGpuRenderer?.() ?? Promise.resolve(),
        canSetRenderer: typeof engine.setRenderer === 'function',
        setRenderer: (kind) => engine.setRenderer?.(kind) ?? Promise.resolve(),
        requestPaint: (viewport, dirty) =>
            dispatch({ type: 'REQUEST_PAINT', viewport, dirty }),

        setZoom: (scale) => dispatch({ type: 'SET_ZOOM', scale }),
        setDeviceScale: (scale) => dispatch({ type: 'SET_DEVICE_SCALE', scale }),
        setViewport: (rect) => dispatch({ type: 'SET_VIEWPORT', rect }),
        expandLayout: (target_y) => dispatch({ type: 'EXPAND_LAYOUT', target_y }),

        undo: () => dispatch({ type: 'UNDO' }),
        redo: () => dispatch({ type: 'REDO' }),

        insertText: (text, at) => dispatch({ type: 'INSERT_TEXT', at, text }),
        deleteRange: (range) => dispatch({ type: 'DELETE_RANGE', range }),
        replaceRange: (range, text) => dispatch({ type: 'REPLACE_RANGE', range, text }),
        deleteAtCaret: (forward, by_word = false) =>
            dispatch({ type: 'DELETE_AT_CARET', forward, by_word }),
        splitParagraph: (at) => dispatch({ type: 'SPLIT_PARAGRAPH', at }),
        /* U+2028 LINE SEPARATOR — the engine's `insert_text` splices it into
         * the paragraph's text run (no block split), and the layout engine
         * forces a line break on it (see `paragraph.rs` `soft_break_segments`). */
        /* Issue #53 — an omitted `at` goes to the wire as `undefined`
         * (= the engine's LIVE caret), not the SDK's async selection
         * mirror, so a soft break racing a caret placement stays put. */
        insertSoftBreak: (at) => dispatch({ type: 'INSERT_TEXT', at, text: '\u2028' }),

        selectAll: () => dispatch({ type: 'SELECT_ALL' }),
        setSelection: (range, caret) => dispatch({ type: 'SET_SELECTION', range, caret }),
        extendSelection: (to) => dispatch({ type: 'EXTEND_SELECTION', to, modifier: 'None' }),
        moveCaret: (direction, extend = false) =>
            dispatch({ type: 'MOVE_CARET', direction, extend }),

        applyAttrs: (patch, range) => fmt(patch, range),
        setBold: (value, range, slot) => fmt(slotted({ bold: value }, slot), range),
        setItalic: (value, range, slot) => fmt(slotted({ italic: value }, slot), range),
        setStrike: (value, range) => fmt({ strike: value }, range),
        setUnderline: (style, range) => fmt({ underline: style }, range),
        /* TextAttrsPatch.script holds a VerticalScript on the wire — the
         * field is named `script` but it carries Super/Sub/Normal. The
         * Sprint 1 (UI Edition) Super/Sub buttons hit this path with no
         * bridge change required. */
        setVerticalScript: (script, range) => fmt({ script }, range),
        setFontFamily: (font_family, range, slot) => fmt(slotted({ font_family }, slot), range),
        setFontSize: (font_size, range, slot) => fmt(slotted({ font_size }, slot), range),
        setSlotFormat: (slot, format, range) => {
            const patch: Partial<TextAttrsPatch> = { font_slot: slot };
            if (format.fontFamily !== undefined) patch.font_family = format.fontFamily;
            if (format.fontSize !== undefined) patch.font_size = format.fontSize;
            if (format.bold !== undefined) patch.bold = format.bold;
            if (format.italic !== undefined) patch.italic = format.italic;
            return fmt(patch, range);
        },
        setColor: (r, g, b, a = 255, range) => fmt({ color: { r, g, b, a } }, range),
        setHighlight: (r, g, b, a = 255, range) =>
            fmt({ bg_color: { r, g, b, a } }, range),
        setCaps: (value, range) => fmt({ caps: value }, range),
        setSmallCaps: (value, range) => fmt({ small_caps: value }, range),
        toggleFormatting: (attr, underlineStyle) =>
            dispatch(
                underlineStyle === undefined
                    ? { type: 'TOGGLE_FORMATTING', attr }
                    : { type: 'TOGGLE_FORMATTING', attr, underline_style: underlineStyle },
            ),

        setParagraphAlign: (align, range) =>
            dispatch({
                type: 'SET_PARAGRAPH_ALIGN',
                range: currentRange(range),
                align,
            }),
        setParagraphDirection: (direction, range) =>
            dispatch({
                type: 'SET_PARAGRAPH_DIRECTION',
                range: currentRange(range),
                direction,
            }),

        insertImage: (image, at, fit = 'FitWidth') =>
            dispatch({ type: 'INSERT_IMAGE', at, image, fit }, [
                image.bytes.buffer as ArrayBuffer,
            ]),
        insertImageAtCaret: (image, fit = 'FitWidth') =>
            dispatch({ type: 'INSERT_IMAGE', at: currentCaret(), image, fit }, [
                image.bytes.buffer as ArrayBuffer,
            ]),
        resizeImage: (path, at, widthEmu, heightEmu, story) =>
            dispatch({
                type: 'RESIZE_IMAGE',
                path,
                at,
                width_emu: widthEmu,
                height_emu: heightEmu,
                story: story ?? [],
            }),
        moveImage: (path, at, offsetHEmu, offsetVEmu, story) =>
            dispatch({
                type: 'MOVE_IMAGE',
                path,
                at,
                offset_h_emu: offsetHEmu,
                offset_v_emu: offsetVEmu,
                story: story ?? [],
            }),
        setImageWrap: (path, at, wrap, story) =>
            dispatch({ type: 'SET_IMAGE_WRAP', path, at, wrap, story: story ?? [] }),
        getImageRects: () => dispatch({ type: 'GET_IMAGE_RECTS' }),

        insertTable: (at, rows, cols) =>
            dispatch({ type: 'INSERT_TABLE', at, rows, cols }),
        insertTableAtCaret: (rows, cols) => {
            /* Resolve the top-level Block step from the caret and insert
             * the table immediately after that block — matches the
             * existing Toolbar.tsx behaviour and keeps nested-cell carets
             * landing outside the table. */
            const sel = state.selection();
            if (!sel) {
                throw new Error(
                    '[@nge/core] insertTableAtCaret requires a caret; no SELECTION_CHANGED yet.',
                );
            }
            const topStep = sel.end.path.steps.find((s) => s.kind === 'BLOCK');
            const idx =
                topStep && topStep.kind === 'BLOCK' ? topStep.idx + 1 : 0;
            return dispatch({
                type: 'INSERT_TABLE',
                at: { steps: [{ kind: 'BLOCK', idx }] },
                rows,
                cols,
            });
        },
        deleteTable: (path) => dispatch({ type: 'DELETE_TABLE', path }),
        insertRow: (table_path, row, side) =>
            dispatch({ type: 'INSERT_ROW', table_path, row, side }),
        deleteRow: (table_path, row) =>
            dispatch({ type: 'DELETE_ROW', table_path, row }),
        insertColumn: (table_path, col, side) =>
            dispatch({ type: 'INSERT_COLUMN', table_path, col, side }),
        deleteColumn: (table_path, col) =>
            dispatch({ type: 'DELETE_COLUMN', table_path, col }),
        mergeCells: (table_path, from_row, from_col, to_row, to_col) =>
            dispatch({
                type: 'MERGE_CELLS',
                table_path,
                from_row,
                from_col,
                to_row,
                to_col,
            }),
        splitCell: (table_path, row, col) =>
            dispatch({ type: 'SPLIT_CELL', table_path, row, col }),
        setCellShading: (table_path, row, col, color) =>
            dispatch({
                type: 'SET_CELL_SHADING',
                table_path,
                row,
                col,
                color,
            }),
        setCellBorders: (table_path, row, col, borders) =>
            dispatch({
                type: 'SET_CELL_BORDERS',
                table_path,
                row,
                col,
                borders,
            }),
        setTableProperties: (table_path, patch) =>
            dispatch({ type: 'SET_TABLE_PROPERTIES', table_path, patch }),
        setTableBidiVisual: (table_path, bidi_visual) =>
            dispatch({
                type: 'SET_TABLE_PROPERTIES',
                table_path,
                patch: { bidi_visual },
            }),

        setColumns: (at, count, gutter_pt) =>
            dispatch({ type: 'SET_COLUMNS', at, count, gutter_pt }),
        setColumnsAtCaret: (count, gutter_pt = 36) =>
            dispatch({
                type: 'SET_COLUMNS',
                at: currentCaret(),
                count,
                gutter_pt,
            }),
        insertPageBreak: (at) =>
            dispatch({ type: 'INSERT_PAGE_BREAK', at: at ?? currentCaret() }),
        insertSectionBreak: (kind, at) =>
            dispatch({
                type: 'INSERT_SECTION_BREAK',
                at: at ?? currentCaret(),
                kind,
            }),
        enterHeaderFooter: (page, area) =>
            dispatch({ type: 'ENTER_HEADER_FOOTER', page, area }),
        exitHeaderFooter: () => dispatch({ type: 'EXIT_HEADER_FOOTER' }),
        setHeaderFooterLink: (linked) =>
            dispatch({ type: 'SET_HEADER_FOOTER_LINK', linked }),
        setTitlePage: (enabled) => dispatch({ type: 'SET_TITLE_PAGE', enabled }),
        setEvenOddHeaders: (enabled) =>
            dispatch({ type: 'SET_EVEN_ODD_HEADERS', enabled }),
        insertField: (kind, at) =>
            dispatch({
                type: 'INSERT_FIELD',
                at: at ?? currentCaret(),
                kind,
            }),
        updateFields: () => dispatch({ type: 'UPDATE_FIELDS' }),
        setFieldCodeView: (enabled) =>
            dispatch({ type: 'SET_FIELD_CODE_VIEW', enabled }),
        setFieldInstruction: (instruction, at) =>
            dispatch({
                type: 'SET_FIELD_INSTRUCTION',
                at: at ?? currentCaret(),
                instruction,
            }),
        insertToc: (switches, at) =>
            dispatch({
                type: 'INSERT_TOC',
                at: at ?? currentCaret(),
                switches: { ...DEFAULT_TOC_SWITCHES, ...switches },
            }),
        insertFootnote: (at) =>
            dispatch({ type: 'INSERT_FOOTNOTE', at: at ?? currentCaret() }),
        insertEndnote: (at) =>
            dispatch({ type: 'INSERT_ENDNOTE', at: at ?? currentCaret() }),
        insertTextBox: (widthEmu = 1_828_800, heightEmu = 914_400, at) =>
            dispatch({
                type: 'INSERT_TEXT_BOX',
                at: at ?? currentCaret(),
                width_emu: widthEmu,
                height_emu: heightEmu,
            }),
        setParagraphBorders: (borders, range) =>
            dispatch({
                type: 'SET_PARAGRAPH_BORDERS',
                range: currentRange(range),
                borders,
            }),
        toggleList: (kind, range) =>
            dispatch({
                type: 'TOGGLE_LIST',
                range: currentRange(range),
                kind,
            }),

        setParagraphIndent: (start_pt, end_pt, first_line_pt, range) =>
            dispatch({
                type: 'SET_PARAGRAPH_INDENT',
                range: currentRange(range),
                start_pt,
                end_pt,
                first_line_pt,
            }),
        /* Indent +/- buttons step `start_pt` (the logical leading-edge
         * indent — `<w:start>`) from the live read-back
         * (`SELECTION_CHANGED.paragraph_indent`), clamped ≥ 0, and pass
         * the other two `<w:ind>` fields through unchanged — the engine
         * takes the whole block atomically (mirrors Ruler.tsx's
         * commitDrag). */
        increaseIndent: (step_pt = 36, range) => {
            const ind = state.paragraphIndent();
            return dispatch({
                type: 'SET_PARAGRAPH_INDENT',
                range: currentRange(range),
                start_pt: Math.max(0, ind.start_pt + step_pt),
                end_pt: ind.end_pt,
                first_line_pt: ind.first_line_pt,
            });
        },
        decreaseIndent: (step_pt = 36, range) => {
            const ind = state.paragraphIndent();
            return dispatch({
                type: 'SET_PARAGRAPH_INDENT',
                range: currentRange(range),
                start_pt: Math.max(0, ind.start_pt - step_pt),
                end_pt: ind.end_pt,
                first_line_pt: ind.first_line_pt,
            });
        },
        setLineSpacing: (multiplier, range) =>
            dispatch({
                type: 'SET_LINE_SPACING',
                range: currentRange(range),
                multiplier,
            }),
        setParagraphShading: (color, range) =>
            dispatch({
                type: 'SET_PARAGRAPH_SHADING',
                range: currentRange(range),
                color,
            }),
        setTabStops: (stops, range) =>
            dispatch({
                type: 'SET_TAB_STOPS',
                range: currentRange(range),
                stops,
            }),

        toggleTrackChanges: (enabled) =>
            dispatch({ type: 'TOGGLE_TRACK_CHANGES', enabled }),
        setReviewIdentity: (author, date) =>
            dispatch({ type: 'SET_REVIEW_IDENTITY', author, date }),
        snapshot: () => dispatch({ type: 'SNAPSHOT', seq: undefined }),
        /* Issue #304 — `revision_id` is omitted, never `undefined`, when
           absent (`exactOptionalPropertyTypes`). */
        acceptRevision: (block, start, end, revisionId) =>
            dispatch({
                type: 'ACCEPT_REVISION',
                block,
                start,
                end,
                ...(revisionId === undefined ? {} : { revision_id: revisionId }),
            }),
        rejectRevision: (block, start, end, revisionId) =>
            dispatch({
                type: 'REJECT_REVISION',
                block,
                start,
                end,
                ...(revisionId === undefined ? {} : { revision_id: revisionId }),
            }),
        acceptAllRevisions: () => dispatch({ type: 'ACCEPT_ALL_REVISIONS' }),
        rejectAllRevisions: () => dispatch({ type: 'REJECT_ALL_REVISIONS' }),
        insertComment: (text, author, range) =>
            dispatch({
                type: 'INSERT_COMMENT',
                range: currentRange(range),
                text,
                author,
            }),
        deleteComment: (id) => dispatch({ type: 'DELETE_COMMENT', id }),
        resolveComment: (id, resolved) =>
            dispatch({ type: 'RESOLVE_COMMENT', id, resolved }),
        replyToComment: (parentId, text, author) =>
            dispatch({
                type: 'REPLY_TO_COMMENT',
                parent_id: parentId,
                text,
                author,
            }),
        applyStyle: (styleId, range) =>
            dispatch({
                type: 'APPLY_STYLE',
                range: currentRange(range),
                style_id: styleId,
            }),
        modifyStyle: (styleId, properties) =>
            dispatch({ type: 'MODIFY_STYLE', style_id: styleId, properties }),

        setPageMargins: (at, top_pt, right_pt, bottom_pt, left_pt) =>
            dispatch({
                type: 'SET_PAGE_MARGINS',
                at,
                top_pt,
                right_pt,
                bottom_pt,
                left_pt,
            }),
        setPageMarginsAtCaret: (top_pt, right_pt, bottom_pt, left_pt) =>
            dispatch({
                type: 'SET_PAGE_MARGINS',
                at: currentCaret(),
                top_pt,
                right_pt,
                bottom_pt,
                left_pt,
            }),
        setPageOrientation: (at, orientation) =>
            dispatch({ type: 'SET_PAGE_ORIENTATION', at, orientation }),
        setPageOrientationAtCaret: (orientation) =>
            dispatch({
                type: 'SET_PAGE_ORIENTATION',
                at: currentCaret(),
                orientation,
            }),

        clearParagraphBorders: (range) =>
            dispatch({
                type: 'SET_PARAGRAPH_BORDERS',
                range: currentRange(range),
                borders: {
                    top: undefined,
                    left: undefined,
                    bottom: undefined,
                    right: undefined,
                },
            }),

        openDocument: async (bytes, name, opts) => {
            /* Issue #239 — queued by the engine even if no `RenderPage`
               has run yet, so this is safe regardless of boot order. */
            if (opts?.initialZoom !== undefined) {
                await dispatch({ type: 'SET_ZOOM', scale: opts.initialZoom });
            }
            /* Issue #221 — an explicit `opts.defaults` wins; otherwise
               fall back to the host's `<EngineProvider documentDefaults>`
               default. Neither set ⇒ the `defaults` key is left off the
               wire object entirely (the pre-#221 shape) — `defaults` on
               `Command::OpenDocument` is `#[tsify(optional)]`, so under
               `exactOptionalPropertyTypes` the key must be OMITTED, not
               set to `undefined` (see `getSelectionAsClipboard` above for
               the same pattern with `include_docx`). */
            const defaults = opts?.defaults ?? documentDefaults;
            const format = opts?.format ?? docFormatForFileName(name);
            const open: Command = {
                type: 'OPEN_DOCUMENT',
                bytes,
                format,
                name: name ?? undefined,
                ...(defaults !== undefined ? { defaults } : {}),
                /* Issue #345 — omitted, never `undefined`, when absent. */
                ...(opts?.password !== undefined ? { password: opts.password } : {}),
            };
            return dispatch(open, [bytes.buffer as ArrayBuffer]);
        },
        saveDocument: (format) => dispatch({ type: 'SAVE_DOCUMENT', format }),
        saveDocx: () => dispatch({ type: 'SAVE_DOCX' }),
        exportPdf: (conformance) => dispatch({ type: 'EXPORT_PDF', conformance }),
        exportHtml: () => dispatch({ type: 'SAVE_DOCUMENT', format: 'html' }),
        exportPlainText: () =>
            dispatch({ type: 'SAVE_DOCUMENT', format: 'plain_text' }),
        closeDocument: async () => {
            const evt = await dispatch({ type: 'CLOSE_DOCUMENT' });
            /* Issue #406 - the closed document's reader warnings go with it. */
            if (evt.type !== 'ERROR') clearOpenWarnings(engine);
            return evt;
        },

        getSelectionAsClipboard: (opts) =>
            dispatch(
                opts?.includeDocx === undefined
                    ? { type: 'GET_SELECTION_AS_CLIPBOARD' }
                    : { type: 'GET_SELECTION_AS_CLIPBOARD', include_docx: opts.includeDocx },
            ),
        pastePlain: (text) => dispatch({ type: 'PASTE_PLAIN', text }),
        pasteHtml: (html) => dispatch({ type: 'PASTE_HTML', html }),

        raw: (cmd, transfer = []) => dispatch(cmd, transfer),
    };
}

/**
 * Returns the typed command facade bound to the engine + state supplied
 * via context. Internally instantiates a `createEditorState` so the
 * formatting / caret helpers can auto-resolve `range` from the current
 * selection. Components that also need to read state should call
 * `createEditorState()` themselves — subscriptions are cheap.
 */
/** Issue #81 — Word's Insert › Table of Contents defaults
 *  (`TOC \o "1-3" \h \z \u`), merged under `insertToc`'s overrides. */
export const DEFAULT_TOC_SWITCHES: TocSwitches = {
    outline_min: 1,
    outline_max: 3,
    hyperlinks: true,
    hide_in_web: true,
    use_outline_levels: true,
    page_numbers: true,
};

export function createEditorCommands(): EditorCommands {
    const engine = useEngine();
    const state = createEditorState();
    /* Issue #221 — the host's `<EngineProvider documentDefaults={...}>`
     * fallback for `openDocument`'s `defaults` option; `undefined` outside
     * any provider, which is the common, non-error case here. */
    const documentDefaults = useDocumentDefaults();
    return build(engine, state, documentDefaults);
}
